import React, { useState, useEffect, useRef } from "react";
import { updateCalendarEntry } from "../../api/schoolConfigApi";
import { showSuccess, showError } from "../../utils/notifications";
import { getContextualError, logError } from "../../utils/errorHandling";
import { formatCalendarDate } from "../../utils/calendarDate";
import { isSplitVariant } from "../../utils/scheduleVariant";
import type { ScheduleVariantGroup } from "../../utils/scheduleVariant";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "../common/Modal";
import type { components } from "../../api/types.gen";
import styles from "./CalendarEntryEditModal.module.css";

type SchoolCalendarResponse = components["schemas"]["SchoolCalendarResponse"];

// Edits a SchoolCalendar entry's schedule type in place via PATCH
// /api/schools/:schoolId/calendar/:id, instead of the delete-and-re-add flow
// the Calendar list's Remove button otherwise requires. Reuses
// ScheduleConfigPanel's groupScheduleTypes() output (`variantGroups`) so the
// "Schedule Type" select shows one option per variant name rather than the
// raw scheduleTypes list, which would show two identically-labeled options
// for a split (Block) variant's A/B halves — the A/B choice becomes its own
// select, shown only when the selected variant is split.
//
// Modal/ModalHeader/ModalBody/ModalFooter are the shared modal chrome
// component from ../common/Modal.tsx (also used by ScheduleVariantModal.js)
// — see this file's own .module.css for the classes specific to this
// modal's sizing/layout.

/**
 * A named variant group as produced by ScheduleConfigPanel's
 * groupScheduleTypes(). Reuses the shared {@link ScheduleVariantGroup}
 * schedule-type-id shape and adds the display name the select renders.
 * Declared here (rather than imported from ScheduleConfigPanel) to avoid a
 * circular import — that panel imports this modal to render it.
 */
export interface CalendarVariantGroup extends ScheduleVariantGroup {
  variantName: string;
}

export interface CalendarEntryEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEntrySaved: () => void;
  schoolId?: number | string | null;
  entry: SchoolCalendarResponse | null;
  variantGroups: CalendarVariantGroup[];
}

function findGroupForScheduleTypeId(
  variantGroups: CalendarVariantGroup[],
  scheduleTypeId: number | null,
): CalendarVariantGroup | null {
  if (scheduleTypeId == null) return null;
  return (
    variantGroups.find(
      (v) =>
        v.scheduleTypeIds.single === scheduleTypeId ||
        v.scheduleTypeIds.aDay === scheduleTypeId ||
        v.scheduleTypeIds.bDay === scheduleTypeId,
    ) || null
  );
}

const CalendarEntryEditModal = ({
  isOpen,
  onClose,
  onEntrySaved,
  schoolId,
  entry,
  variantGroups,
}: CalendarEntryEditModalProps): React.ReactElement | null => {
  const [loading, setLoading] = useState(false);
  const [selectedVariantName, setSelectedVariantName] = useState("");
  const [selectedAbDay, setSelectedAbDay] = useState("A");

  // Identity of the (open, entry) pair we last seeded the selection from, so
  // we only re-seed when the modal (re)opens or the entry itself changes —
  // not merely because the parent handed us a fresh `variantGroups` array.
  const seededForRef = useRef<SchoolCalendarResponse | null>(null);

  useEffect(() => {
    if (!isOpen || !entry) {
      seededForRef.current = null;
      return;
    }

    // A new variantGroups identity with the same entry (e.g. the parent
    // re-fetched or re-rendered) must NOT clobber an in-progress selection
    // that still names a live variant. Only re-seed on a genuine open/entry
    // change, or when the current selection no longer exists in the groups.
    const alreadySeeded = seededForRef.current === entry;
    const selectionStillValid =
      selectedVariantName === "" ||
      variantGroups.some((v) => v.variantName === selectedVariantName);
    if (alreadySeeded && selectionStillValid) return;

    seededForRef.current = entry;

    const group = findGroupForScheduleTypeId(
      variantGroups,
      entry.scheduleTypeId,
    );
    if (!group) {
      setSelectedVariantName("");
      setSelectedAbDay("A");
      return;
    }

    setSelectedVariantName(group.variantName);
    setSelectedAbDay(
      group.scheduleTypeIds.bDay === entry.scheduleTypeId ? "B" : "A",
    );
  }, [isOpen, entry, variantGroups, selectedVariantName]);

  if (!isOpen || !entry) return null;

  const selectedGroup = variantGroups.find(
    (v) => v.variantName === selectedVariantName,
  );
  const isSplit = isSplitVariant(selectedGroup);

  const handleSave = async (): Promise<void> => {
    if (schoolId == null) return;

    let scheduleTypeId: number | null | undefined;
    if (!selectedGroup) {
      // "No School" — an intentional clear, not a missing id.
      scheduleTypeId = null;
    } else if (isSplit) {
      const side = selectedAbDay === "B" ? "bDay" : "aDay";
      scheduleTypeId = selectedGroup.scheduleTypeIds[side];
      // A split variant can legitimately have only one of its A/B halves
      // (isSplitVariant is true when EITHER aDay or bDay exists). Selecting
      // the missing half used to resolve to a nullish id that JSON.stringify
      // drops entirely, sending a no-op PATCH. Surface it instead of silently
      // doing nothing.
      if (scheduleTypeId == null) {
        showError(
          `"${selectedGroup.variantName}" has no ${selectedAbDay} Day schedule. Please choose the other day.`,
        );
        return;
      }
    } else {
      scheduleTypeId = selectedGroup.scheduleTypeIds.single;
    }

    setLoading(true);
    try {
      await updateCalendarEntry(schoolId, entry.id, { scheduleTypeId });
      showSuccess(`Updated schedule for ${formatCalendarDate(entry.date)}`);
      onEntrySaved();
      onClose();
    } catch (error) {
      logError(error, "updateCalendarEntry");
      showError(
        getContextualError(
          error,
          undefined,
          "Failed to update calendar entry. Please try again.",
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      className={styles["calendar-entry-edit-modal"]}
      onClose={loading ? undefined : onClose}
    >
      <ModalHeader
        title={`Edit ${formatCalendarDate(entry.date)}`}
        onClose={onClose}
        disabled={loading}
      />

      <ModalBody className={styles["modal-body-content"]}>
        <label>
          Schedule Type:
          <select
            value={selectedVariantName}
            onChange={(e) => {
              setSelectedVariantName(e.target.value);
              setSelectedAbDay("A");
            }}
            className={styles["schedule-select"]}
            disabled={loading}
          >
            <option value="">No School</option>
            {variantGroups.map((v) => (
              <option key={v.variantName} value={v.variantName}>
                {v.variantName}
              </option>
            ))}
          </select>
        </label>

        {isSplit && (
          <label>
            A Day / B Day:
            <select
              value={selectedAbDay}
              onChange={(e) => setSelectedAbDay(e.target.value)}
              className={styles["schedule-select"]}
              disabled={loading}
            >
              <option value="A">A Day</option>
              <option value="B">B Day</option>
            </select>
          </label>
        )}
      </ModalBody>

      <ModalFooter>
        <button
          onClick={onClose}
          className={styles["btn-secondary"]}
          disabled={loading}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          className={styles["btn-primary"]}
          disabled={loading}
        >
          {loading ? "Saving..." : "Save Changes"}
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default CalendarEntryEditModal;
